import { FormData } from "../types";
import { toProperCase } from "../../../../../helper/company_helper";

export const validateReportTypeForm = (
  formData: FormData,
  setValidationErrors: any
): boolean => {
  const errors: string[] = [];
  if (!formData.name.trim()) errors.push("Report name is required.");
  if (
    (formData.frequencyType === "daily" ||
      formData.frequencyType === "weekly" ||
      formData.frequencyType === "monthly") &&
    formData.days.length === 0
  )
    errors.push("Select at least one day for daily/weekly frequency.");
  const weeklyIntNum = Number(formData.weeklyInterval);
  const monthlyIntNum = Number(formData.monthlyInterval);
  if (
    (formData.frequencyType === "weekly" &&
      (isNaN(weeklyIntNum) || weeklyIntNum < 1 || weeklyIntNum > 52)) ||
    (formData.frequencyType === "monthly" &&
      (isNaN(monthlyIntNum) || monthlyIntNum < 1 || monthlyIntNum > 12))
  )
    errors.push("Repeat interval is invalid.");

  if (!formData.startDate) errors.push("Start Date is required.");

  if (!formData.status) errors.push("Status is required.");
  setValidationErrors(errors);
  return errors.length === 0;
};

export const validateFieldReportTypeForm = (
  field: string,
  data: FormData
): string[] => {
  const errors: string[] = [];

  switch (field) {
    case "name":
      if (!data.name.trim()) errors.push("Report name is required.");
      break;

    case "days":
      if (
        (data.frequencyType === "daily" || data.frequencyType === "weekly") &&
        data.days.length === 0
      ) {
        errors.push("Select at least one day for daily/weekly frequency.");
      }
      break;

    case "weeklyInterval":
      const weeklyIntNum = Number(data.weeklyInterval);
      if (
        data.frequencyType === "weekly" &&
        (data.weeklyInterval === "" ||
          isNaN(weeklyIntNum) ||
          weeklyIntNum < 1 ||
          weeklyIntNum > 52)
      ) {
        errors.push("Repeat interval must be between 1 and 52.");
      }
      break;

    case "monthlyInterval":
      const monthlyIntNum = Number(data.monthlyInterval);
      if (
        data.frequencyType === "monthly" &&
        (data.monthlyInterval === "" ||
          isNaN(monthlyIntNum) ||
          monthlyIntNum < 1 ||
          monthlyIntNum > 12)
      ) {
        errors.push("Repeat interval must be between 1 and 12.");
      }
      break;

    case "startDate":
      if (!data.startDate) errors.push("Start Date is required.");
      break;

    case "status":
      if (!data.status) errors.push("Status is required.");
      break;
  }

  return errors;
};

export const updateFieldValidationReportTypeForm = (
  field: string,
  data: FormData,
  setValidationErrors: any
) => {
  const fieldErrors = validateFieldReportTypeForm(field, data);

  setValidationErrors((prevErrors: string[]) => {
    const otherFieldErrors = prevErrors.filter((error) => {
      const fieldErrorPatterns = {
        name: ["Report name is required."],
        days: ["Select at least one day for daily/weekly frequency."],
        reportsPerWeek: [
          "Reports per week must be between 1 and number of selected days.",
        ],
        weeklyInterval: ["Repeat interval must be between 1 and 52."],
        reportsPerMonth: ["Reports per month must be between 1 and 31."],
        monthlyDates: ["Enter at least one date for monthly frequency."],
        monthlyInterval: ["Repeat interval must be between 1 and 12."],
        startDate: ["Start Date is required."],
        status: ["Status is required."],
      };

      const patterns =
        fieldErrorPatterns[field as keyof typeof fieldErrorPatterns] || [];
      return !patterns.some((pattern) => error.includes(pattern.split(" ")[0]));
    });
    return [...otherFieldErrors, ...fieldErrors];
  });
};

// Multi-select handlers
export const handleDepartmentAdd = (
  departmentKey: any,
  onChange: any,
  formData: any
) => {
  const updatedDepartments = [...formData.department, departmentKey];
  onChange("department", updatedDepartments);
};

export const handleDepartmentRemove = (
  departmentKey: any,
  onChange: any,
  formData: any
) => {
  const updatedDepartments = formData.department.filter(
    (key: number) => key !== departmentKey
  );
  onChange("department", updatedDepartments);
};

export const handleEmployeeAdd = (
  employeeKey: any,
  onChange: any,
  formData: any
) => {
  const updatedEmployees = [...formData.showsTo, employeeKey];
  onChange("showsTo", updatedEmployees);
};

export const handleEmployeeRemove = (
  employeeKey: any,
  onChange: any,
  formData: any
) => {
  const updatedEmployees = formData.showsTo.filter(
    (key: number) => key !== employeeKey
  );
  onChange("showsTo", updatedEmployees);
};

export const handleDesignationAdd = (
  designationKey: any,
  onChange: any,
  formData: any
) => {
  const updatedDesignations = [...formData.designation, designationKey];
  onChange("designation", updatedDesignations);
};

export const handleDesignationRemove = (
  designationKey: any,
  onChange: any,
  formData: any
) => {
  const updatedDesignations = formData.designation.filter(
    (key: number) => key !== designationKey
  );
  onChange("designation", updatedDesignations);
};

// Helper functions for getting department, employees, designations
export const getDepartmentOptions = <
  T extends { key: number; Department: string }
>(
  departments: T[]
) => {
  return (
    departments?.map((department) => ({
      value: department.key,
      label: department.Department,
    })) || []
  );
};

export const getFullName = (
  salutation: string | any,
  firstName: string | any,
  lastName: string | any
) => {
  const parts = [salutation, firstName, lastName]
    .map((value) => {
      if (!value || value === "null" || value === "undefined") return "";
      return typeof value === "string" ? toProperCase(value.trim()) : "";
    })
    .filter(Boolean); // Removes empty strings

  return parts.join(" ");
};

export const getEmployeeOptions = <
  T extends {
    id: number;
    salutation: any;
    fname: any;
    lname: any;
    profile_pic: any;
    email: any;
  }
>(
  employees: T[]
) => {
  return (
    employees?.map((emp) => ({
      value: emp.id,
      label: `${getFullName(null, emp?.fname, emp?.lname)} @${
        emp?.email?.split("@")?.[0]
      }`,
      image_url: emp.profile_pic,
    })) || []
  );
};

export const getDesignationOptions = (
  designations: any[],
  departmentIds: any[] = [],
  departments: any[] = []
): any[] => {
  const selectedDepartments =
    departments.filter((elem) => departmentIds.includes(elem?.key)) || [];

  const departmentsMap = new Map();
  selectedDepartments.forEach((elem) => {
    if (!departmentsMap.get(elem.key)) {
      departmentsMap.set(elem.key, elem);
    }
  });

  const result: any[] = [];

  designations?.forEach((designation) => {
    if (departmentsMap.get(designation?.Department_id)) {
      result.push({
        value: designation.key,
        label: designation.Designation,
      });
    }
  });

  return result;
};
